import { useI18n } from 'core/hooks/useI18n';
import { HighchartsReactRefObject as ChartRef, HighchartsReact } from 'highcharts-react-official';
import HighchartsStock from 'highcharts/esm/highstock';
import type { Options } from 'highcharts/highstock';
import { useEffect, useMemo, useRef, HTMLAttributes, RefObject } from 'react';
import { ChartWrapper, StyledChart } from './Chart.styles';

import 'highcharts/esm/modules/no-data-to-display';

type ChartProps = {
  options: Options;
  isLoading?: boolean;
  containerProps?: HTMLAttributes<HTMLDivElement>;
  chartRef?: RefObject<ChartRef>;
} & HTMLAttributes<HTMLDivElement>;

// This is no longer valid, unsure how to fix for now
// override hasData method as it does not handle windbarb series correctly
// HighchartsStock.Chart.prototype.hasData = function () {
//   return this.series.some(series => {
//     return series.hasData() || !!series.points.length;
//   });
// };

export const Chart = ({ options, isLoading, containerProps, chartRef, ...rest }: ChartProps) => {
  const internalRef = useRef<ChartRef>(null);
  const effectiveRef = chartRef ?? internalRef;
  const containerRef = useRef<HTMLDivElement>(null);
  const translations = useTranslations();
  const chartOptions: Options = useMemo(
    () => ({
      ...options,
      lang: {
        ...translations,
        ...options.lang,
      },
    }),
    [options, translations]
  );
  const mergedContainerProps = useMergedContainerProps({ containerProps, containerEl: containerRef.current });

  useLoadingState({ chartRef: effectiveRef.current, isLoading });
  useResizeObserver(containerRef, () => {
    effectiveRef.current?.chart?.reflow();
  });

  return (
    <ChartWrapper ref={containerRef}>
      <StyledChart {...rest}>
        <HighchartsReact
          highcharts={HighchartsStock}
          ref={effectiveRef}
          options={chartOptions}
          containerProps={mergedContainerProps}
          // The key is used to force re-rendering of the chart when series change
          key={chartOptions.series?.length}
        />
      </StyledChart>
    </ChartWrapper>
  );
};

/**
 * Ensure width and height are set so chart can stretch to fill the dimensions of its container
 */
function useMergedContainerProps({
  containerProps,
  containerEl,
}: {
  containerProps: ChartProps['containerProps'];
  containerEl: HTMLDivElement | null;
}) {
  if (!containerEl) return;
  const { width, height } = containerEl.getBoundingClientRect();
  const { style, ...rest } = containerProps ?? {};
  if (style) {
    const { width: w, height: h } = style;
    return {
      ...rest,
      style: {
        ...style,
        width: w ?? width,
        height: h ?? height,
      },
    };
  }
  return {
    style: {
      width,
      height,
    },
    ...rest,
  };
}

function useLoadingState({ chartRef, isLoading }: { chartRef: ChartRef | null; isLoading?: boolean }) {
  useEffect(() => {
    if (!chartRef) return;
    if (isLoading) {
      chartRef.chart?.showLoading();
    } else {
      chartRef.chart?.hideLoading();
    }
  }, [isLoading, chartRef]);
}

function useTranslations() {
  const { l10n } = useI18n('app/components', 'highcharts');
  const noData = l10n('noData').split(' ');
  // Add line break to prevent text overflow on smaller charts
  noData.splice(Math.floor(noData.length / 2), 0, '<br />');

  return useMemo(
    () => ({
      loading: l10n('loading'),
      noData: noData.join(' '),
      resetZoom: l10n('resetZoom'),
      resetZoomTitle: l10n('resetZoomTitle'),
    }),
    [l10n, noData]
  );
}

const useResizeObserver = (ref: React.RefObject<HTMLDivElement>, callback: () => void) => {
  useEffect(() => {
    if (!ref.current) return;
    const resizeObserver = new ResizeObserver(() => {
      callback();
    });
    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, [ref, callback]);
};
